import {
  Calendar,
  Gauge,
  MapPin,
  User,
  Edit,
  Trash2,
  Wrench,
} from "lucide-react";
import { formatDateShort } from "@/utils/dateFormat";
import { useTranslation } from "react-i18next";
import type { VehicleMaintenance } from "@/types";

interface VehicleMaintenanceListProps {
  maintenanceRecords: VehicleMaintenance[];
  isLoading: boolean;
  onEdit: (maintenance: VehicleMaintenance) => void;
  onDelete: (maintenanceId: string) => void;
  deleteConfirm: string | null;
}

export function VehicleMaintenanceList({
  maintenanceRecords,
  isLoading,
  onEdit,
  onDelete,
  deleteConfirm,
}: VehicleMaintenanceListProps) {
  const { t } = useTranslation(["pages", "common"]);
  if (isLoading) {
    return (
 <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
 <div key={i} className="animate-pulse">
 <div className="bg-secondary-100 rounded-lg p-4">
 <div className="flex justify-between items-start mb-3">
 <div className="space-y-2 flex-1">
 <div className="h-4 bg-secondary-200 rounded w-1/3"></div>
 <div className="h-3 bg-secondary-200 rounded w-1/2"></div>
                </div>
 <div className="h-8 bg-secondary-200 rounded w-16"></div>
              </div>
 <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
 <div className="h-3 bg-secondary-200 rounded"></div>
 <div className="h-3 bg-secondary-200 rounded"></div>
 <div className="h-3 bg-secondary-200 rounded"></div>
 <div className="h-3 bg-secondary-200 rounded"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (maintenanceRecords.length === 0) {
    return (
      <div className="text-center py-12">
        <Wrench className="h-12 w-12 text-secondary-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-secondary-900 mb-2">
          {t("pages:vehicles.maintenance.listEmptyTitle")}
        </h3>
        <p className="text-secondary-500">
          {t("pages:vehicles.maintenance.listEmptyBody")}
        </p>
      </div>
    );
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case "scheduled":
        return "bg-green-100 text-green-800";
      case "unexpected":
        return "bg-red-100 text-red-800";
      case "preventative":
        return "bg-primary-100 text-primary-800";
      case "other":
        return "bg-secondary-100 text-secondary-800";
      default:
        return "bg-secondary-100 text-secondary-800";
    }
  };

  const formatMileage = (mileage?: number) => {
    if (mileage === undefined || mileage === null)
      return t("pages:vehicles.maintenance.mileageNotRecorded");
    // Use en-US locale for consistent formatting across environments
    return t("pages:vehicles.maintenance.mileageValue", {
      value: mileage.toLocaleString("en-US"),
    });
  };

  return (
 <div className="space-y-4">
      {maintenanceRecords.map((record) => (
        <div
          key={record.id}
 className="bg-secondary-50 border border-secondary-200 rounded-lg p-4 hover:bg-secondary-100 transition-colors"
        >
 <div className="flex justify-between items-start mb-3">
 <div className="flex-1">
 <div className="flex items-center space-x-3 mb-2">
 <h3 className="text-lg font-semibold text-secondary-900">
                  {record.workType}
                </h3>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(record.type)}`}
                >
                  {t(`pages:vehicles.maintenanceTypes.${record.type}`)}
                </span>
              </div>
              {record.description && (
 <p className="text-sm text-secondary-600 mb-3">
                  {record.description}
                </p>
              )}
            </div>

 <div className="flex items-center space-x-2">
            <button
                onClick={() => onEdit(record)}
                className="p-2 text-secondary-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                title={t("pages:vehicles.maintenance.editTitle")}
                aria-label={t("pages:vehicles.maintenance.editAria")}
              >
                <Edit className="h-4 w-4" />
              </button>
              <button
                onClick={() => onDelete(record.id)}
                className={`p-2 rounded-lg transition-colors ${
                  deleteConfirm === record.id
                    ? "text-red-600 bg-red-50"
                    : "text-secondary-400 hover:text-red-600 hover:bg-red-50"
                }`}
                title={t("pages:vehicles.maintenance.deleteTitle")}
                aria-label={t("pages:vehicles.maintenance.deleteAria")}
              >
 <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-secondary-400" />
              <div>
                <div className="text-secondary-500">{t("pages:vehicles.maintenance.fields.date")}</div>
                <div className="font-medium text-secondary-900">
                  {formatDateShort(record.performedAt)}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Gauge className="h-4 w-4 text-secondary-400" />
              <div>
                <div className="text-secondary-500">{t("pages:vehicles.maintenance.fields.mileage")}</div>
                <div className="font-medium text-secondary-900">
                  {formatMileage(record.mileage)}
                </div>
              </div>
            </div>

            {record.shopName && (
              <div className="flex items-center space-x-2">
                <MapPin className="h-4 w-4 text-secondary-400" />
                <div>
                  <div className="text-secondary-500">{t("pages:vehicles.maintenance.fields.shop")}</div>
                  <div className="font-medium text-secondary-900">
                    {record.shopName}
                  </div>
                </div>
              </div>
            )}

            {record.maintainers && (
              <div className="flex items-center space-x-2">
                <User className="h-4 w-4 text-secondary-400" />
                <div>
                  <div className="text-secondary-500">{t("pages:vehicles.maintenance.fields.maintainer")}</div>
                  <div className="font-medium text-secondary-900">
                    {record.maintainers}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-3 pt-3 border-t border-secondary-200 text-xs text-secondary-500">
            <span>{t("pages:vehicles.maintenance.createdAt", { date: formatDateShort(record.createdAt) })}</span>
            {record.updatedAt && record.updatedAt !== record.createdAt && (
              <span className="ml-4">
                {t("pages:vehicles.maintenance.updatedAt", { date: formatDateShort(record.updatedAt) })}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
